/**
 * MORVIX AI — local consulting engine.
 *
 * Intent resolution, session memory, the consultative discovery flow and the
 * project qualification flow all live here. It is deliberately isolated from
 * the UI and from the transport layer (see ./api.ts) so the same conversation
 * can later be answered by a hosted model without any component changes.
 */

import {
  CTA,
  briefFields,
  discoveryQuestions,
  knowledge,
  policyReplies,
  serviceById,
  serviceGuide,
} from '../../data/consultant'
import { brand, contact, links, whatsappUrl } from '../../data/site'
import type { BriefAnswers, ConsultantAction, ConsultantReply, ConsultantSession, EngineResult } from './types'

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const normalize = (value: string) => ` ${value.toLowerCase().replace(/[^a-z0-9$&+/\s]/g, ' ').replace(/\s+/g, ' ').trim()} `

const hasAny = (haystack: string, needles: string[]) =>
  needles.some((needle) => haystack.includes(` ${needle} `) || haystack.includes(`${needle} `) || haystack.includes(` ${needle}`))

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())

const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

/* ------------------------------------------------------------------ */
/* Intent detection                                                    */
/* ------------------------------------------------------------------ */

const NEED_CUES = ['i need', 'i want', 'looking for', 'need a', 'need some', 'need help', 'help me with', 'can you do', 'do you do', 'interested in', 'require']
const PRICE_CUES = ['price', 'pricing', 'cost', 'how much', 'quote', 'budget', 'rate', 'charge', 'fee', 'expensive', 'afford']
const START_CUES = ['start a project', 'start my project', 'get started', 'begin', 'book', 'booking', 'consultation', 'hire', 'work with you', 'brief', 'sign up', 'lets go', 'ready to']
const GREETING_CUES = ['hi', 'hey', 'hello', 'good morning', 'good afternoon', 'good evening', 'yo', 'sup']
const THANKS_CUES = ['thanks', 'thank you', 'appreciate', 'cheers', 'awesome', 'perfect', 'great']
const HUMAN_CUES = ['human', 'real person', 'speak to someone', 'talk to a person', 'agent']
/* Business-policy cues — these are never answered with figures or commitments. */
const DELIVERY_CUES = ['how long', 'how many days', 'how many weeks', 'turnaround', 'turn around', 'delivery time', 'deliver', 'delivered', 'delivery', 'timeline', 'time frame', 'timeframe', 'deadline', 'how fast', 'how soon', 'when can you', 'when will']
const REVISION_CUES = ['revision', 'revisions', 'revise', 'how many changes', 'request changes', 'make changes', 'unlimited changes', 'rounds of changes', 'amendments']
const TERMS_CUES = ['contract', 'agreement', 'deposit', 'invoice', 'refund', 'payment terms', 'negotiate', 'discount', 'discounts', 'offer me', 'guarantee', 'guaranteed', 'guarantees', 'money back', 'warranty', 'promise']
/** Questions about *how* to begin — answered with guidance, not a form. */
const HOW_START_CUES = ['how do i start', 'how do we start', 'how to start', 'how do i begin', 'how do we begin', 'where do i start', 'first step', 'next step', 'next steps', 'how do i work with you', 'how do we work together']

function detectService(text: string): string | undefined {
  let bestId: string | undefined
  let bestScore = 0
  for (const service of serviceGuide) {
    for (const alias of service.aliases) {
      const matched =
        text.includes(` ${alias} `) || text.includes(`${alias} `) || text.includes(` ${alias}`)
      if (matched && alias.length > bestScore) {
        bestScore = alias.length
        bestId = service.id
      }
    }
  }
  return bestId
}

function detectTopic(text: string): string | undefined {
  let bestId: string | undefined
  let bestScore = 0
  for (const topic of knowledge) {
    let score = 0
    for (const keyword of topic.keywords) {
      // Whole-phrase matching keeps short keywords from firing inside longer words.
      if (hasAny(text, [keyword])) score += keyword.length
    }
    if (score > bestScore) {
      bestScore = score
      bestId = topic.id
    }
  }
  return bestScore >= 4 ? bestId : undefined
}

/* ------------------------------------------------------------------ */
/* Conversational memory & smart follow-ups                            */
/* ------------------------------------------------------------------ */

/** Remembers every service the visitor has raised, in the order they raised it. */
function remember(session: ConsultantSession, serviceId: string) {
  const seen = session.mentioned ?? []
  session.mentioned = seen.includes(serviceId) ? seen : [...seen, serviceId]
}

type FollowUp = { key: string; question: string; options?: string[] }

/**
 * Picks the single most useful thing we still don't know about the visitor's
 * project, so the consultant qualifies gently instead of interrogating.
 */
function nextFollowUp(session: ConsultantSession): FollowUp | undefined {
  const asked = session.askedFollowUps ?? []
  const focusName = session.focus ? serviceById(session.focus)?.name : undefined
  const candidates: FollowUp[] = [
    {
      key: 'stage',
      question: focusName
        ? `Quick one so I can be useful: is this ${focusName.toLowerCase()} for a brand new business, or one that already exists?`
        : 'Quick one so I can be useful: are you building something new, or refreshing a brand that already exists?',
      options: ['Brand new business', 'Existing business', 'Rebrand / refresh'],
    },
    {
      key: 'industry',
      question: 'And what industry is it in? That shapes the direction more than anything else.',
      options: ['Fashion & apparel', 'Food & drink', 'Tech / SaaS', 'Beauty & wellness', 'Real estate', 'Something else'],
    },
    {
      key: 'scope',
      question: 'Is this a single piece, or part of a wider system across your brand?',
      options: ['Just this one piece', 'Part of a bigger system', 'Not sure — advise me'],
    },
  ]

  for (const candidate of candidates) {
    if (asked.includes(candidate.key)) continue
    if (candidate.key === 'stage' && session.stage) continue
    if (candidate.key === 'industry' && session.industry) continue
    if (candidate.key === 'scope' && session.scope) continue
    return candidate
  }
  return undefined
}

/** Appends one smart follow-up question to a reply, at most once per topic. */
function withFollowUp(reply: ConsultantReply, session: ConsultantSession): ConsultantReply {
  const followUp = nextFollowUp(session)
  if (!followUp) return reply
  session.askedFollowUps = [...(session.askedFollowUps ?? []), followUp.key]
  const extras = (followUp.options ?? []).map(
    (option) => ({ label: option, kind: 'send', value: option }) as ConsultantAction,
  )
  return {
    text: `${reply.text}\n\n${followUp.question}`,
    actions: [...extras, ...(reply.actions ?? [])].slice(0, 5),
  }
}

/** Captures anything useful the visitor says in passing, so we never re-ask. */
function absorbContext(session: ConsultantSession, raw: string, text: string) {
  if (!session.stage && hasAny(text, ['new business', 'brand new', 'just starting', 'startup', 'start up', 'existing business', 'rebrand', 'refresh', 'already have'])) {
    session.stage = raw
  }
  if (!session.industry && hasAny(text, ['fashion', 'apparel', 'clothing', 'food', 'drink', 'restaurant', 'saas', 'tech', 'software', 'beauty', 'wellness', 'skincare', 'real estate', 'property', 'fitness', 'church', 'music'])) {
    session.industry = raw
  }
  if (!session.scope && hasAny(text, ['just this', 'one piece', 'one off', 'full system', 'everything', 'complete system', 'whole brand'])) {
    session.scope = raw
  }
}

/* ------------------------------------------------------------------ */
/* Discovery flow                                                      */
/* ------------------------------------------------------------------ */

function askDiscovery(index: number): ConsultantReply {
  const step = discoveryQuestions[index]
  return {
    text: step.question,
    actions: step.options.map((option) => ({ label: option, kind: 'send', value: option }) as ConsultantAction),
  }
}

function recommendation(session: ConsultantSession): ConsultantReply {
  const focus = session.focus ? serviceById(session.focus) : undefined
  const wantsLogoOnly = /logo only/i.test(session.scope ?? '')
  const wantsSystem = /complete|full|system|identity/i.test(session.scope ?? '')
  const lowBudget = /under \$?300/i.test(session.budget ?? '')

  let recommended = focus
  if (!focus || focus.id === 'brand-identity' || focus.id === 'logo-design' || focus.id === 'creative-strategy') {
    if (wantsLogoOnly || (lowBudget && !wantsSystem)) recommended = serviceById('logo-design')
    else recommended = serviceById('brand-identity')
  }

  const target = recommended ?? serviceById('brand-identity')!
  const context = [
    session.stage ? session.stage.toLowerCase() : null,
    session.industry && !/something else/i.test(session.industry) ? `in ${session.industry.toLowerCase()}` : null,
  ]
    .filter(Boolean)
    .join(' ')

  const budgetNote = lowBudget
    ? `\n\nWith your current range I'd rather start focused and do it properly — we can grow it into the full system later without redrawing anything.`
    : ''

  return {
    text: `Based on everything you've told me${context ? ` — ${context} —` : ''} I'd recommend our **${target.name}** package.\n\n${target.what}\n\nWhat you'd walk away with:\n${target.deliverables
      .map((item) => `• ${item}`)
      .join('\n')}${budgetNote}\n\nPricing and delivery schedules are always confirmed by the team once they've reviewed your requirements — request a custom quote or complete our contact form and they'll come back to you.`,
    actions: [CTA.quote, CTA.consult, CTA.contactUs, CTA.whatsapp],
  }
}

/* ------------------------------------------------------------------ */
/* Brief flow                                                          */
/* ------------------------------------------------------------------ */

function askBrief(index: number, session: ConsultantSession): ConsultantReply {
  const field = briefFields[index]
  // Pre-fill from what we already know, so we never make the visitor repeat.
  const known =
    field.key === 'service' && session.focus
      ? serviceById(session.focus)?.name
      : field.key === 'industry'
        ? session.industry
        : field.key === 'budget'
          ? session.budget
          : undefined

  const options = field.options ? [...field.options] : []
  if (known && !options.includes(known)) options.unshift(known)
  if (field.optional) options.push('Skip')

  return {
    text: known ? `${field.question}${options.length ? `\n\n(You mentioned ${known} earlier — tap it to confirm.)` : ''}` : field.question,
    actions: options.map((option) => ({ label: option, kind: 'send', value: option }) as ConsultantAction),
  }
}

export function buildBriefSummary(brief: BriefAnswers) {
  return [
    `New project inquiry via Morvix AI`,
    `Name: ${brief.name ?? '—'}`,
    `Email: ${brief.email ?? '—'}`,
    `Company / brand: ${brief.company ?? '—'}`,
    `Industry: ${brief.industry ?? '—'}`,
    `Service needed: ${brief.service ?? '—'}`,
    `Estimated budget: ${brief.budget ?? '—'}`,
    `Timeline: ${brief.timeline ?? '—'}`,
    `Goals: ${brief.goals ?? '—'}`,
    `Notes: ${brief.notes ?? '—'}`,
  ].join('\n')
}

export function briefWhatsAppUrl(brief: BriefAnswers) {
  return `https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(buildBriefSummary(brief))}`
}

export function briefMailtoUrl(brief: BriefAnswers) {
  const subject = `Project inquiry — ${brief.company ?? brief.name ?? 'New brand'}`
  return `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildBriefSummary(brief))}`
}

function briefComplete(session: ConsultantSession): ConsultantReply {
  const b = session.brief
  return {
    text: `That's everything I need, ${b.name?.split(' ')[0] ?? 'thank you'} — here's what I have:\n\n• Brand: ${b.company}\n• Industry: ${b.industry}\n• Service: ${b.service}\n• Budget: ${b.budget}\n• Timeline: ${b.timeline}\n• Goals: ${b.goals}\n\nPick whichever way you'd like to send it through — WhatsApp is the fastest, and the team replies personally.`,
    actions: [
      { label: 'Continue on WhatsApp', kind: 'link', value: briefWhatsAppUrl(b) },
      { label: 'Send by email', kind: 'link', value: briefMailtoUrl(b) },
      { label: 'Submit the contact form', kind: 'route', value: '/contact' },
    ],
  }
}

/* ------------------------------------------------------------------ */
/* Service answers                                                     */
/* ------------------------------------------------------------------ */

function serviceAnswer(id: string, session: ConsultantSession): ConsultantReply {
  const service = serviceById(id)!
  // Conversational memory: acknowledge anything discussed earlier in the chat.
  const earlier = (session.mentioned ?? []).filter((item) => item !== id)
  const previous = earlier.length ? serviceById(earlier[earlier.length - 1]) : undefined
  const continuity = previous
    ? `Yes — and it sits naturally next to the ${previous.name.toLowerCase()} we were just talking about, since both run off the same design language.\n\n`
    : ''

  return {
    text: `${continuity}**${service.name}** — ${service.tagline}.\n\n${service.what}\n\nDeliverables:\n${service.deliverables
      .map((item) => `• ${item}`)
      .join('\n')}${
      previous
        ? `\n\nIf it's all for the same project, the team usually scopes ${previous.name.toLowerCase()} and ${service.name.toLowerCase()} together so nothing gets designed twice.`
        : ''
    }`,
    actions: [
      { label: 'Yes — help me decide', kind: 'send', value: `help me choose ${service.name}` },
      CTA.quote,
      CTA.portfolio,
    ],
  }
}

/* ------------------------------------------------------------------ */
/* Business policy answers — no pricing, no dates, no commitments      */
/* ------------------------------------------------------------------ */

/** Pricing, quotes, discounts and package costs always go to the team. */
function pricingAnswer(): ConsultantReply {
  return { text: policyReplies.pricing.text, actions: [...policyReplies.pricing.actions] }
}

/** Delivery schedules are confirmed by the team, never by the consultant. */
function deliveryAnswer(): ConsultantReply {
  return { text: policyReplies.delivery.text, actions: [...policyReplies.delivery.actions] }
}

/** Revision policy is part of the workflow conversation with the team. */
function revisionsAnswer(): ConsultantReply {
  return { text: policyReplies.revisions.text, actions: [...policyReplies.revisions.actions] }
}

/** Contracts, terms and other project-specific decisions → team handoff. */
function handoffAnswer(): ConsultantReply {
  return { text: policyReplies.handoff.text, actions: [...policyReplies.handoff.actions] }
}

/* ------------------------------------------------------------------ */
/* Main entry point                                                    */
/* ------------------------------------------------------------------ */

export function respond(input: string, previous: ConsultantSession): EngineResult {
  const session: ConsultantSession = {
    ...previous,
    brief: { ...previous.brief },
    answered: [...previous.answered],
    mentioned: [...(previous.mentioned ?? [])],
    askedFollowUps: [...(previous.askedFollowUps ?? [])],
    turns: (previous.turns ?? 0) + 1,
  }
  const raw = input.trim()
  const text = normalize(raw)
  const replies: ConsultantReply[] = []

  /* ---- Escape hatches available in any flow ---- */
  if (hasAny(text, ['cancel', 'stop', 'nevermind', 'never mind']) && session.flow !== 'idle') {
    session.flow = 'idle'
    session.discoveryStep = 0
    session.briefStep = 0
    return {
      replies: [
        {
          text: "No problem — we'll park that. Ask me anything else whenever you're ready.",
          actions: [CTA.servicesPage, CTA.portfolio, CTA.whatsapp],
        },
      ],
      session,
    }
  }

  /* ---- Qualification flow ---- */
  if (session.flow === 'brief') {
    const field = briefFields[session.briefStep]
    const skipping = field.optional && /^(skip|none|no|nothing)$/i.test(raw)

    if (field.validate === 'email' && !skipping && !isEmail(raw)) {
      return {
        replies: [{ text: "That doesn't look like a complete email address — could you double-check it? The team needs somewhere to send your proposal." }],
        session,
      }
    }
    if (!skipping && raw.length < 2) {
      return { replies: [{ text: `Sorry, I didn't catch that. ${field.question}` }], session }
    }

    session.brief[field.key] = skipping ? '—' : raw
    if (field.key === 'industry') session.industry = raw
    if (field.key === 'budget') session.budget = raw
    if (field.key === 'service') {
      const detected = detectService(normalize(raw))
      if (detected) {
        session.focus = detected
        remember(session, detected)
      }
    }

    const nextIndex = session.briefStep + 1
    if (nextIndex >= briefFields.length) {
      session.flow = 'idle'
      session.briefStep = 0
      return { replies: [briefComplete(session)], session, briefComplete: true }
    }
    session.briefStep = nextIndex
    return { replies: [askBrief(nextIndex, session)], session }
  }

  /* ---- Consultative discovery flow ---- */
  if (session.flow === 'discovery') {
    const step = discoveryQuestions[session.discoveryStep]
    if (raw.length < 1) return { replies: [askDiscovery(session.discoveryStep)], session }

    if (step.key === 'stage') session.stage = raw
    if (step.key === 'industry') session.industry = raw
    if (step.key === 'scope') session.scope = raw
    if (step.key === 'budget') session.budget = raw

    const nextIndex = session.discoveryStep + 1
    if (nextIndex >= discoveryQuestions.length) {
      session.flow = 'idle'
      session.discoveryStep = 0
      return { replies: [recommendation(session)], session }
    }
    session.discoveryStep = nextIndex
    return { replies: [askDiscovery(nextIndex)], session }
  }

  /* ---- Idle: intent resolution ---- */
  const service = detectService(text)
  if (service) {
    session.focus = service
    remember(session, service)
  }
  absorbContext(session, raw, text)

  // "How do I start?" — explain the path before pulling anyone into a brief
  if (hasAny(text, HOW_START_CUES)) {
    const topic = knowledge.find((t) => t.id === 'getting-started')!
    return { replies: [{ text: topic.answer, actions: topic.actions }], session }
  }

  // Start / book / quote
  if (hasAny(text, START_CUES)) {
    session.flow = 'brief'
    session.briefStep = 0
    replies.push({
      text: "Love that. I'll take a short brief so the team can come back to you with something specific rather than generic.",
    })
    replies.push(askBrief(0, session))
    return { replies, session }
  }

  // Explicit request for guidance → discovery
  if (hasAny(text, ['help me choose', 'help me decide', 'not sure', 'dont know', 'what do i need', 'recommend', 'advise', 'which service'])) {
    session.flow = 'discovery'
    session.discoveryStep = 0
    replies.push({ text: "Happy to. A few quick questions and I'll give you a straight recommendation." })
    replies.push(askDiscovery(0))
    return { replies, session }
  }

  // Pricing, quotes, discounts — never answered with figures
  if (hasAny(text, PRICE_CUES) && !hasAny(text, ['portfolio', 'process'])) {
    return { replies: [pricingAnswer()], session }
  }

  // Delivery time / turnaround — never answered with a timeframe
  if (hasAny(text, DELIVERY_CUES) && !hasAny(text, ['portfolio', 'process', 'about'])) {
    return { replies: [deliveryAnswer()], session }
  }

  // Revisions — workflow discussed by the team, not here
  if (hasAny(text, REVISION_CUES)) {
    return { replies: [revisionsAnswer()], session }
  }

  // Contracts, payment terms, negotiation → straight to the team
  if (hasAny(text, TERMS_CUES)) {
    return { replies: [handoffAnswer()], session }
  }

  // "I need branding" → consultative discovery
  if (service && hasAny(text, NEED_CUES)) {
    session.flow = 'discovery'
    session.discoveryStep = 0
    const guide = serviceById(service)!
    replies.push({ text: `${guide.name} — good place to start. ${guide.tagline}.` })
    replies.push(askDiscovery(0))
    return { replies, session }
  }

  // Human handoff
  if (hasAny(text, HUMAN_CUES)) {
    return {
      replies: [
        {
          text: `Of course — I'm here to make the first step easy, not to replace the team. You can reach ${brand.name} directly on WhatsApp at ${contact.whatsappDisplay} or by email at ${contact.email}.`,
          actions: [CTA.whatsapp, CTA.contact],
        },
      ],
      session,
    }
  }

  // Knowledge base topic
  const topicId = detectTopic(text)
  if (topicId) {
    const topic = knowledge.find((t) => t.id === topicId)!
    if (!session.answered.includes(topic.id)) session.answered.push(topic.id)
    const reply: ConsultantReply = { text: topic.answer, actions: topic.actions }
    /* Policy answers hand over to the team — no qualifying questions attached. */
    const policyTopic = ['pricing-general', 'timeline', 'revisions', 'payment', 'getting-started'].includes(topic.id)
    return { replies: [policyTopic ? reply : withFollowUp(reply, session)], session }
  }

  // Plain service mention — answered in the context of the wider conversation
  if (service) {
    return { replies: [withFollowUp(serviceAnswer(service, session), session)], session }
  }

  // Greeting
  if (raw.length < 24 && hasAny(text, GREETING_CUES)) {
    return {
      replies: [
        {
          text: `Hey — good to meet you. Tell me what you're working on and I'll point you in the right direction. If you'd rather I ask the questions, just say “help me choose”.`,
          actions: [
            { label: 'Explore Our Services', kind: 'send', value: 'Explore Our Services' },
            { label: 'Help me choose', kind: 'send', value: 'help me choose a service' },
            CTA.portfolio,
          ],
        },
      ],
      session,
    }
  }

  // Thanks
  if (hasAny(text, THANKS_CUES) && raw.length < 40) {
    return {
      replies: [
        {
          text: "Anytime. If you'd like, I can take a short brief and get the team looking at it today.",
          actions: [CTA.brief, CTA.whatsapp],
        },
      ],
      session,
    }
  }

  /* ---- Graceful fallback ---- */
  const focusName = session.focus ? serviceById(session.focus)?.name : undefined
  const fallback: ConsultantReply = {
    text: `${
      focusName
        ? `I want to give you a properly useful answer on that alongside your ${focusName.toLowerCase()} project rather than guess.`
        : "I want to give you a properly useful answer rather than guess at that one."
    } I'm strongest on our services, our process, our portfolio and getting your project started — anything to do with pricing, schedules or project terms I'll pass to the team so you get accurate information.`,
    actions: [
      { label: 'Explore Our Services', kind: 'send', value: 'Explore Our Services' },
      { label: 'Our process', kind: 'send', value: 'what is your process' },
      CTA.whatsapp,
    ],
  }
  return { replies: [withFollowUp(fallback, session)], session }
}

/** First message(s) shown when the panel is opened for the first time. */
export function openingReplies(intro: string, quick: string[]): ConsultantReply[] {
  return [
    {
      text: intro,
      actions: quick.map((label) => {
        if (label === 'View Portfolio') return { label, kind: 'link', value: links.portfolio } as ConsultantAction
        if (label === 'Contact Us') return { label, kind: 'route', value: '/contact' } as ConsultantAction
        return { label, kind: 'send', value: label } as ConsultantAction
      }),
    },
  ]
}

export const consultantWhatsApp = whatsappUrl
export const consultantTitle = titleCase(brand.short)
